// HCEN Patient Website — API Service
// Gateway: HCEN_Main_API only (ds5hf43t45) — same as Flutter app.
// Hospital dashboard uses a SEPARATE gateway (fg0d020oeg → HCEN_Website_API).

const BASE = (process.env.REACT_APP_API_URL ||
  'https://ds5hf43t45.execute-api.ap-southeast-2.amazonaws.com/prod')
  .replace(/\/$/, '');     // strip trailing slash

const authHeader = () => {
  const t = localStorage.getItem('hcen_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function req(method, path, body) {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { error: data.message || `Error ${res.status}`, data: null };
    return { data, error: null };
  } catch (e) {
    return { error: e.message || 'Network error', data: null };
  }
}

export const api = {

  // — Hospitals ————————————————————————————————————————
  getHospitals: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const r  = await req('GET', `/hospitals${qs ? '?' + qs : ''}`);
    if (r.error) return { data: [], error: r.error };
    // API may return { hospitals:[...] } OR [...] directly
    const list = r.data?.hospitals ?? r.data?.items ?? r.data?.data ?? r.data;
    return { data: Array.isArray(list) ? list : [], error: null };
  },

  getHospital: (id) => req('GET', `/hospitals/${id}`),

  // — Doctors ——————————————————————————————————————————
  getDoctors: (hospitalId) => req('GET', `/hospitals/${hospitalId}/doctors`),

  // — Slots ————————————————————————————————————————————
  getSlots: (hospitalId, doctorId, date) =>
    req('GET', `/hospitals/${hospitalId}/doctors/${doctorId}/available-slots?date=${date}`),

  // — Bookings —————————————————————————————————————————
  bookDoctor: (body) => req('POST', '/bookings', body),
  bookWalkin: (body) => req('POST', '/bookings', { ...body, type: body.type || 'walkin', bookingType: body.bookingType || 'walkin' }),
  getMyBookings: (patientId) =>
    req('GET', `/bookings?patientId=${encodeURIComponent(patientId)}&userId=${encodeURIComponent(patientId)}`),

  getBooking: (bookingId) =>
    req('GET', `/bookings/${encodeURIComponent(bookingId)}`),

  /** Fan-out phone + UUID variants so bookings always resolve (same as Flutter + prescriptions). */
  getMyBookingsAll: async (user) => {
    if (!user) return { data: [], error: 'not logged in' };
    const phoneDigits = (user.phone || '').replace(/\D/g, '');
    const d10 = phoneDigits.length >= 10 ? phoneDigits.slice(-10) : phoneDigits;
    const ids = [...new Set([
      user.patientId, user.usersId, user.userId,
      phoneDigits, d10,
      d10 ? `91${d10}` : '', d10 ? `+91${d10}` : '',
      d10 ? `phone_91${d10}@hcen.app` : '',
    ].filter(Boolean))];

    const seen = new Set();
    const merged = [];
    let lastError = null;

    for (const id of ids) {
      const r = await req('GET', `/bookings?patientId=${encodeURIComponent(id)}&userId=${encodeURIComponent(id)}`);
      if (r.error) { lastError = r.error; continue; }
      const list = Array.isArray(r.data) ? r.data : (r.data?.bookings || r.data?.items || []);
      for (const b of (Array.isArray(list) ? list : [])) {
        const k = b.bookingIds || b.bookingId || b.id;
        if (!k || seen.has(k)) continue;
        seen.add(k);
        merged.push(b);
      }
    }

    merged.sort((a, b) => {
      const ta = new Date(a.createdAt || a.dateTime || a.date || 0).getTime();
      const tb = new Date(b.createdAt || b.dateTime || b.date || 0).getTime();
      return tb - ta;
    });

    return { data: merged, error: merged.length ? null : lastError };
  },
  getBookedSlots: (doctorId, date, hospitalId) =>
    req('GET', '/bookings/slots?doctorId=' + encodeURIComponent(doctorId) + '&date=' + encodeURIComponent(date) + (hospitalId ? '&hospitalId=' + encodeURIComponent(hospitalId) : '')),
  rescheduleBooking: (bookingId, _newDate, newSlot) =>
    req('PATCH', '/bookings/reschedule', { bookingId, newSlot }),
  cancelBooking: (id) => req('POST', `/bookings/${id}/cancel`),

  // — Emergency / SOS (HCEN_Main_API / ds5hf43t45) —————————
  // POST /emergencies/sos — progressive radius broadcast (5→50 km every 45s)
  sendSosBroadcast: (body) => req('POST', '/bookings', { ...body, type: 'sos' }),
  // POST /emergency — direct alert to one hospital
  sendEmergency: (body) => req('POST', '/emergency', body),
  // GET /emergency/{alertId} — live status poll
  getEmergencyStatus: (alertId) => req('GET', `/emergency/${encodeURIComponent(alertId)}`),
  // POST /emergency/{alertId}/cancel — patient cancels SOS
  cancelEmergency: (alertId) => req('POST', `/emergency/${encodeURIComponent(alertId)}/cancel`),

  // — Auth OTP (via Lambda -> MSG91 WhatsApp, same as Android) —
  // NOTE: AuthContext owns the active OTP flow now (with delivery-failure
  // handling). These wrappers stay here for callers that import { api }.
  sendOtp: async (phone) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    return req('POST', '/auth/otp/send', { phone: digits });
  },
  verifyOtp: async (phone, code) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    // Lambda reads `body.otp` — keep `code` for forward compat.
    return req('POST', '/auth/otp/verify', { phone: digits, otp: code, code });
  },

  // — Offers & Health Camps ————————————————————————————
  // Backend stores both regular offers and health-camp banners in the
  // HealthOffers table. We classify a row as a "camp" when its badge or title
  // contains the word "camp" so the UI can split them visually.
  getOffers: async ({ city, state, hospitalId } = {}) => {
    const params = {};
    if (city)  params.city  = city;
    if (state) params.state = state;
    const qs = new URLSearchParams(params).toString();
    const r  = await req('GET', `/offers${qs ? '?' + qs : ''}`);
    if (r.error) return { data: [], error: r.error };
    let list = r.data?.offers ?? r.data ?? [];
    if (!Array.isArray(list)) list = [];
    if (hospitalId) list = list.filter(o => o.hospitalId === hospitalId);
    return { data: list, error: null };
  },

  // — User Profile —————————————————————————————————————
  // GET /users/me?phone=XXXXXXXXXX -> { patientId, name, phone, email, age?, gender?, address? }
  getMe: async (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return { data: null, error: 'phone required' };
    return req('GET', `/users/me?phone=${encodeURIComponent(digits)}`);
  },
  // PATCH /users/me — accepts any subset of name/age/gender/address/email/fcmToken
  updateMe: async (phone, updates) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits) return { data: null, error: 'phone required' };
    return req('PATCH', '/users/me', { phone: digits, ...updates });
  },

  // — Support Tickets ——————————————————————————————————
  // Endpoints on HCEN_Main_API:
  //   POST  /tickets                 — raise a new ticket
  //   GET   /tickets/my?phone=&userId= — list my tickets
  //   GET   /tickets/{id}            — ticket detail with thread
  //   POST  /tickets/{id}/messages   — add follow-up message
  //   PATCH /tickets/{id}/reopen     — reopen a resolved ticket
  //   PATCH /tickets/{id}/rate       — rate after resolved
  createTicket: (body) => req('POST', '/tickets', body),
  myTickets: ({ phone, userId }) => {
    const p = new URLSearchParams();
    if (phone)  p.set('phone', String(phone).replace(/\D/g, '').slice(-10));
    if (userId) p.set('userId', userId);
    return req('GET', `/tickets/my?${p.toString()}`);
  },
  getTicket:    (ticketId)        => req('GET',   `/tickets/${encodeURIComponent(ticketId)}`),
  replyTicket:  (ticketId, text)  => req('POST',  `/tickets/${encodeURIComponent(ticketId)}/messages`, { text }),
  reopenTicket: (ticketId)        => req('PATCH', `/tickets/${encodeURIComponent(ticketId)}/reopen`),
  rateTicket:   (ticketId, rating, comment) =>
    req('PATCH', `/tickets/${encodeURIComponent(ticketId)}/rate`, { rating, comment }),

  // — Refer & Earn —————————————————————————————————————
  // GET /referrals/me?usersId=...&phone=... -> { code, stats, shareMessage, shareLink, terms, rewardAmount, cap }
  myReferrals: ({ usersId, phone }) => {
    const p = new URLSearchParams();
    if (usersId) p.set('usersId', usersId);
    if (phone)   p.set('phone',   String(phone).replace(/\D/g, '').slice(-10));
    return req('GET', `/referrals/me?${p.toString()}`);
  },

  // — Reviews ——————————————————————————————————————————
  // POST /reviews                            — submit
  // GET  /reviews/pending?patientId=...      — bookings awaiting a review
  // GET  /reviews/booking/{bookingId}        — existing review for a booking
  pendingReviews: (patientId) =>
    req('GET', `/reviews/pending?patientId=${encodeURIComponent(patientId)}`),
  submitReview:   (body) => req('POST', '/reviews', body),
  getBookingReview: (bookingId) =>
    req('GET', `/reviews/booking/${encodeURIComponent(bookingId)}`),
  hospitalReviewSummary: (hospitalId) =>
    req('GET', `/reviews/hospital/${encodeURIComponent(hospitalId)}/summary`),
  hospitalReviews: (hospitalId, limit = 8) =>
    req('GET', `/reviews/hospital/${encodeURIComponent(hospitalId)}?limit=${limit}`),
  doctorReviewSummary: (doctorId) =>
    req('GET', `/reviews/doctor/${encodeURIComponent(doctorId)}/summary`),
  doctorReviews: (doctorId, limit = 8) =>
    req('GET', `/reviews/doctor/${encodeURIComponent(doctorId)}?limit=${limit}`),

  // — Prescriptions ————————————————————————————————————
  getMyPrescriptions: (patientId) =>
    req('GET', `/prescriptions/my?patientId=${encodeURIComponent(patientId)}`),
  /** Fan-out phone + UUID variants (same pattern as My Bookings). */
  getMyPrescriptionsAll: async (user) => {
    if (!user) return { data: [], error: 'not logged in' };
    const phoneDigits = (user.phone || '').replace(/\D/g, '');
    const d10 = phoneDigits.length >= 10 ? phoneDigits.slice(-10) : phoneDigits;
    const ids = [...new Set([
      user.patientId, phoneDigits, d10,
      d10 ? `91${d10}` : '', d10 ? `+91${d10}` : '',
    ].filter(Boolean))];
    const seen = new Set();
    const merged = [];
    for (const id of ids) {
      const r = await req('GET', `/prescriptions/my?patientId=${encodeURIComponent(id)}`);
      const list = r.data?.prescriptions ?? (Array.isArray(r.data) ? r.data : []);
      for (const rx of (Array.isArray(list) ? list : [])) {
        const k = rx.prescriptionId || `${rx.bookingId}-${rx.createdAt}`;
        if (seen.has(k)) continue;
        seen.add(k);
        merged.push(rx);
      }
    }
    merged.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    return { data: merged, error: null };
  },
  getPrescription: (bookingId) =>
    req('GET', `/prescriptions?bookingId=${encodeURIComponent(bookingId)}`),

  // — Loyalty Points ———————————————————————————————————
  getLoyalty: (patientId) =>
    req('GET', `/loyalty?patientId=${encodeURIComponent(patientId)}`),
  redeemLoyalty: (body) =>
    req('POST', '/loyalty/redeem', body),

  // — Transactions —————————————————————————————————————
  getTransactions: (phone) =>
    req('GET', `/transactions?phone=${encodeURIComponent(phone)}`),
  getTransaction: (txnId) =>
    req('GET', `/transactions/${encodeURIComponent(txnId)}`),

  // — Promo Codes ——————————————————————————————————————
  validatePromo: (body) =>
    req('POST', '/coupons/validate', { couponId: body.code || body.couponId, amount: body.amount, hospitalId: body.hospitalId }),
  applyPromo: (body) =>
    req('POST', '/coupons/validate', { couponId: body.code || body.couponId, amount: body.amount, hospitalId: body.hospitalId }),

  // — Coupons ——————————————————————————————————————————
  getMyCoupons: (patientId, phone) => {
    const p = new URLSearchParams();
    if (patientId) p.set('usersId', patientId);
    if (phone) p.set('phone', String(phone).replace(/\D/g, '').slice(-10));
    return req('GET', `/coupons/my?${p.toString()}`);
  },

  
  // — Razorpay Payments ————————————————————————————————
  createRazorpayOrder: (body) =>
    req('POST', '/payments/razorpay/create-order', body),
  verifyRazorpayPayment: (body) =>
    req('POST', '/payments/razorpay/verify', body),

  // — Find Doctor by specialization (aggregates across hospitals) —
  // The Lambda has no global doctor index; we reuse /hospitals + per-hospital
  // /doctors fan-out. This is fine for the current catalog size and matches
  // how the Flutter app surfaces specialists.
  findDoctorsBySpecialization: async (specialization) => {
    const { specMatches } = await import('../utils/specAliases');
    const { hospitalId } = await import('../utils/hospitalFilters');
    const needle = (specialization || '').trim();
    if (!needle) return { data: [], error: null };

    const hospR = await req('GET', '/hospitals');
    const raw = hospR.data?.hospitals ?? hospR.data?.items ?? hospR.data?.data ?? hospR.data;
    const hospitals = Array.isArray(raw) ? raw : [];
    if (!hospitals.length) return { data: [], error: hospR.error };

    const results = await Promise.all(hospitals.map(async (h) => {
      const hid = hospitalId(h);
      if (!hid) return [];
      const r = await req('GET', `/hospitals/${encodeURIComponent(hid)}/doctors`);
      const docs = r.data?.doctors ?? r.data ?? [];
      const matched = (Array.isArray(docs) ? docs : []).filter(d =>
        specMatches(needle, d.specialization || '')
      );
      return matched.map(d => ({
        ...d,
        id:           d.id || d.doctorId,
        doctorId:     d.doctorId || d.id,
        hospitalId:   hid,
        hospitalName: h.name || '',
        hospitalCity: h.city || '',
        hospitalState: h.state || '',
        fee:          d.fee || d.consultationFee,
      }));
    }));
    return { data: results.flat(), error: null };
  },
};
